'use client';

export interface QuoteConfirmationData {
  email: string;
  fullName: string;
  businessType: string;
  containerSize: string;
  location: string;
  timeline: string;
  message?: string;
}

export interface FollowUpReminderData {
  email: string;
  fullName: string;
  daysAgo: number;
  bookingUrl: string;
}

export interface AdminQuoteNotificationData {
  adminEmail: string;
  fullName: string;
  email: string;
  phone: string;
  businessType: string;
  containerSize: string;
  location: string;
  timeline: string;
  message?: string;
  adminDashboardUrl: string;
}

export interface AdminLeadNotificationData {
  adminEmail: string;
  fullName: string;
  email: string;
  phone: string;
  source: string;
  businessType?: string;
  adminDashboardUrl: string;
}

export interface AppointmentConfirmationCustomerData {
  email: string;
  fullName: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
  notes?: string;
}

export interface AppointmentConfirmationAdminData {
  adminEmail: string;
  fullName: string;
  email: string;
  phone: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
  notes?: string;
  managerPortalUrl: string;
}

export interface AppointmentReminderData {
  email: string;
  fullName: string;
  appointmentDate: string;
  appointmentTime: string;
  location: string;
}

export interface LeadFollowUpData {
  email: string;
  fullName: string;
  source: string;
  bookingUrl: string;
}

export interface AdminCreatedQuoteData {
  email: string;
  fullName: string;
  businessType: string;
  containerSize: string;
  location: string;
  timeline: string;
  quotedPrice?: number;
  notes?: string;
}

export interface QuoteFileUploadData {
  email: string;
  fullName: string;
  containerSize: string;
  status?: string;
  quotedPrice?: number;
  notes?: string;
  fileCount?: number;
  portalUrl?: string;
}

export interface ManagerQuoteApprovalData {
  managerEmail: string;
  managerName: string;
  customerName: string;
  containerSize: string;
  quotedPrice: number;
  responseType: string;
  notes?: string;
  dashboardUrl: string;
}

export interface NewCustomerRegistrationData {
  adminEmail: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  registrationDate: string;
  portalUrl: string;
}

export interface QuoteNegotiationNotificationData {
  email: string;
  fullName: string;
  containerSize: string;
  originalPrice?: number;
  proposedPrice?: number;
  proposedTimeline?: string;
  customerNotes?: string;
  managerResponse?: string;
  managerCounterPrice?: number;
  managerCounterTimeline?: string;
  portalUrl?: string;
}

export interface PaymentConfirmationData {
  email: string;
  fullName: string;
  amount: number;
  paymentMethod?: string;
  transactionId: string;
  paymentDate?: string;
  invoiceNumber?: string;
  portalUrl?: string;
}

export interface ShipmentUpdateData {
  email: string;
  fullName: string;
  status: string;
  trackingNumber?: string;
  carrier?: string;
  estimatedDelivery?: string;
  currentLocation?: string;
  statusMessage?: string;
  trackingUrl?: string;
}

export interface PasswordResetData {
  email: string;
  fullName?: string;
  resetUrl: string;
}

export interface LeadAssignmentNotificationData {
  assigneeEmail: string;
  assigneeName: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  businessType?: string;
  leadSource: string;
  leadStatus?: string;
  dashboardUrl: string;
}

/**
 * Structured result for every email send. `error` holds a short, UI-friendly
 * reason explaining why a send failed (e.g. unverified sender domain) so any
 * screen can surface it instead of a generic "failed" message.
 */
export interface EmailResult {
  success: boolean;
  error?: string;
}

/**
 * Convert a raw provider/server error string into a short, human-readable
 * reason that is safe to display directly in the UI.
 */
function humanizeEmailError(raw: string): string {
  const text = (raw || '').toString();
  const lower = text.toLowerCase();

  // Pull out the underlying provider (Resend) message if the error embeds JSON.
  let detail = text;
  const jsonStart = text.indexOf('{');
  if (jsonStart !== -1) {
    try {
      const parsed = JSON.parse(text.slice(jsonStart));
      detail = parsed?.message || parsed?.error || detail;
    } catch {
      // Keep the original text if it isn't valid JSON.
    }
  }
  const detailLower = detail.toLowerCase();

  if (
    lower.includes('domain is not verified') ||
    lower.includes('not verified') ||
    detailLower.includes('verify a domain') ||
    detailLower.includes('verify your domain')
  ) {
    return 'Sender domain is not verified with the email provider. Verify your domain (or sender address) in Resend before sending.';
  }
  if (lower.includes('resend_api_key') || lower.includes('api key') || lower.includes('api_key')) {
    return 'Email service is not configured (missing or invalid Resend API key).';
  }
  if (lower.includes('you can only send testing emails') || lower.includes('testing emails to your own')) {
    return 'Resend is in test mode and can only send to your own verified email address.';
  }
  if (detailLower.includes('invalid') && detailLower.includes('email')) {
    return 'The recipient email address is invalid.';
  }
  if (lower.includes('missing recipient')) {
    return 'No recipient email address was provided.';
  }
  if (lower.includes('rate limit') || lower.includes('too many requests')) {
    return 'Email provider rate limit reached. Please try again in a moment.';
  }
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('network error')) {
    return 'Could not reach the email service. Check your connection and try again.';
  }

  const cleaned = detail.replace(/^Resend API error:\s*/i, '').trim();
  if (!cleaned) return 'Unknown email error.';
  return cleaned.length > 180 ? `${cleaned.slice(0, 177)}...` : cleaned;
}

/**
 * Single entry point that talks to the send-email edge function. Every named
 * helper below delegates here so error handling/parsing is consistent across
 * the entire app.
 */
async function dispatchEmail(emailType: string, data: unknown): Promise<EmailResult> {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emailType, data }),
    });

    if (!response.ok) {
      let rawError = `Request failed with status ${response.status}`;
      try {
        const body = await response.json();
        rawError = body?.error || JSON.stringify(body);
      } catch {
        try {
          rawError = (await response.text()) || rawError;
        } catch {
          // Keep the default status-based message.
        }
      }
      console.error(`Failed to send ${emailType} email:`, rawError);
      return { success: false, error: humanizeEmailError(rawError) };
    }

    return { success: true };
  } catch (error) {
    const rawError = error instanceof Error ? error.message : 'Unknown error';
    console.error(`Error sending ${emailType} email:`, error);
    return { success: false, error: humanizeEmailError(rawError) };
  }
}

export const emailService = {
  /** Low-level escape hatch for sending any email type with a structured result. */
  dispatch: dispatchEmail,

  sendQuoteConfirmation(data: QuoteConfirmationData): Promise<EmailResult> {
    return dispatchEmail('quote_confirmation', data);
  },

  sendFollowUpReminder(data: FollowUpReminderData): Promise<EmailResult> {
    return dispatchEmail('follow_up_reminder', data);
  },

  sendAdminQuoteNotification(data: AdminQuoteNotificationData): Promise<EmailResult> {
    return dispatchEmail('admin_new_quote', data);
  },

  sendAdminLeadNotification(data: AdminLeadNotificationData): Promise<EmailResult> {
    return dispatchEmail('admin_new_lead', data);
  },

  sendAppointmentConfirmationToCustomer(
    data: AppointmentConfirmationCustomerData
  ): Promise<EmailResult> {
    return dispatchEmail('appointment_confirmation_customer', data);
  },

  sendAppointmentConfirmationToAdmin(
    data: AppointmentConfirmationAdminData
  ): Promise<EmailResult> {
    return dispatchEmail('appointment_confirmation_admin', data);
  },

  sendAppointmentReminder(
    data: AppointmentReminderData,
    reminderType: '24h' | '1h'
  ): Promise<EmailResult> {
    return dispatchEmail(
      reminderType === '24h' ? 'appointment_reminder_24h' : 'appointment_reminder_1h',
      data
    );
  },

  sendProgressNotification(data: {
    email: string;
    fullName: string;
    stage: string;
    notes: string;
  }): Promise<EmailResult> {
    return dispatchEmail('progress_notification', data);
  },

  sendLeadFollowUp(data: LeadFollowUpData): Promise<EmailResult> {
    return dispatchEmail('lead_follow_up', data);
  },

  sendQuoteFileUploadNotification(data: QuoteFileUploadData): Promise<EmailResult> {
    return dispatchEmail('quote_file_uploaded', data);
  },

  sendAdminCreatedQuoteNotification(data: AdminCreatedQuoteData): Promise<EmailResult> {
    return dispatchEmail('admin_created_quote', data);
  },

  sendManagerQuoteApproval(data: ManagerQuoteApprovalData): Promise<EmailResult> {
    return dispatchEmail('manager_quote_approval', data);
  },

  sendNewCustomerRegistrationNotification(
    data: NewCustomerRegistrationData
  ): Promise<EmailResult> {
    return dispatchEmail('new_customer_registration', data);
  },

  sendQuoteNegotiationSubmitted(data: QuoteNegotiationNotificationData): Promise<EmailResult> {
    return dispatchEmail('quote_negotiation_submitted', data);
  },

  sendQuoteNegotiationApproved(data: QuoteNegotiationNotificationData): Promise<EmailResult> {
    return dispatchEmail('quote_negotiation_approved', data);
  },

  sendQuoteNegotiationRejected(data: QuoteNegotiationNotificationData): Promise<EmailResult> {
    return dispatchEmail('quote_negotiation_rejected', data);
  },

  sendQuoteNegotiationNotification(
    data: QuoteNegotiationNotificationData
  ): Promise<EmailResult> {
    return dispatchEmail('quote_negotiation_notification', data);
  },

  sendPaymentConfirmation(data: PaymentConfirmationData): Promise<EmailResult> {
    return dispatchEmail('payment_confirmation', data);
  },

  sendShipmentUpdate(data: ShipmentUpdateData): Promise<EmailResult> {
    return dispatchEmail('shipment_update', data);
  },

  sendPasswordReset(data: PasswordResetData): Promise<EmailResult> {
    return dispatchEmail('password_reset', data);
  },

  sendLeadAssignmentNotification(
    data: LeadAssignmentNotificationData
  ): Promise<EmailResult> {
    return dispatchEmail('lead_assignment_notification', data);
  },
};